import React from 'react';
import Intl from '@fx-ui/intl';
import {
    compact,
    getOdmId,
    getQueryRouter,
    getSiteRouter,
    getUrlParameter,
    getUrlSearchParams,
    insertURLQuery,
    isEmpty,
    isNumber,
    localStorageManager,
    post,
    redirect,
    tracker
} from '~/utils';
import {
    API,
    ECode,
    LOCAL_STORAGE,
    LOCAL_STORAGE_MIGRATE,
    SITE_ROUTER,
    TRACKER,
    URL_PARAMETER
} from '~/constants';
import { LoginType } from '~/interfaces';
import LoginWechat from '~/assets/svgicons/account/login-wechat.svg';
import LoginDingtalk from '~/assets/svgicons/account/login-dingtalk.svg';
import LoginWxwork from '~/assets/svgicons/account/login-wxwork.svg';
import LoginSSO from '~/assets/svgicons/account/login-sso.svg';
import LoginFanruan from '~/assets/svgicons/account/login-fanruan.svg';
import LoginLark from '~/assets/svgicons/account/login-lark.svg';
import { MultiBaseEnterpriseCountIcon } from '@fx-ui/icon';
import { Message } from '@fx-ui/fine-design';

type IntegrateLoginUrls = NonNullable<NonNullable<Window['fr_integrate_login_options']>['urls']>;

/**
 * 指定登录方式是否有可用的 Suite
 * @param item 登录方式
 */
const hasLoginSuites = (item: LoginType) => {
    const loginType = item as keyof IntegrateLoginUrls;
    const suites = window.fr_integrate_login_options?.urls?.[loginType];
    return !isEmpty(suites);
};

export const isPhoneCodeLoginCache = () => {
    return (
        localStorageManager.getItemWithMigrate({
            oldKey: LOCAL_STORAGE.SIGN_IN.ACCOUNT,
            newKey: LOCAL_STORAGE_MIGRATE.SIGN_IN_ACCOUNT
        }) === false
    );
};

export const getOtherLoginItemConfig = (
    item: LoginType
): {
    icon?: React.ReactElement;
    text?: string;
    label?: string;
    className?: string;
    onClick?: (callback?: () => void) => void;
    // 当前登录项是否可见
    visible?: boolean;
} => {
    switch (item) {
        case LoginType.Wechat:
            return {
                visible: !isEmpty(window.jdy_wechat_open_config),
                icon: <LoginWechat />,
                text: Intl.get('wechat')
            };
        case LoginType.Dingtalk:
            return {
                visible: !isEmpty(window.jdy_dingtalk_corp_member_url) || hasLoginSuites(item),
                icon: <LoginDingtalk />,
                text: Intl.get('dingtalk'),
                onClick: () => {
                    // 钉钉不保留登录方式，因为是直接跳转的第三方登录页
                    redirect(window.jdy_dingtalk_corp_member_url);
                }
            };
        case LoginType.Wxwork:
            return {
                visible:
                    !isEmpty(window.jdy_wechat_corp_config) ||
                    !isEmpty(window.jdy_wechat_corp_admin_url) ||
                    !isEmpty(window.jdy_wechat_corp_member_url) ||
                    hasLoginSuites(item),
                icon: <LoginWxwork />,
                text: Intl.get('wework')
            };
        case LoginType.SSO:
            return {
                visible: true,
                icon: <LoginSSO />,
                text: Intl.get('sso')
            };
        case LoginType.FanruanPassport:
            return {
                visible: true,
                icon: <LoginFanruan />,
                text: Intl.get('fxp.base.workspace.profile.fanruan_pass.opt'),
                onClick: () => {
                    tracker(TRACKER.LOGIN_BY_FANRUAN_PASS);
                    redirect(getSiteRouter(SITE_ROUTER.AUTHENTICATION.FANRUAN_ISSUE));
                }
            };
        case LoginType.Lark:
            return {
                visible: !isEmpty(window.jdy_lark_qr_auth_url) || hasLoginSuites(item),
                icon: <LoginLark />,
                text: Intl.get('lark'),
                onClick: () => {
                    // 飞书登录直接跳转到第三方扫码页面，不保留登录方式
                    redirect(window.jdy_lark_qr_auth_url);
                }
            };
        case LoginType.Tenant:
            return {
                visible: true,
                icon: <MultiBaseEnterpriseCountIcon className="tenant-login-icon" />,
                label: Intl.get('fxp.base.workspace.login.corp_login.ttl'),
                className: 'full-line has-hover',
                onClick: (callback) => {
                    tracker(TRACKER.SELF_HOSTED_CORP_LOGIN);
                    return post<{ single: boolean; tenantId?: string; current_corp_url: string }>(
                        API.login_page.single_tenant_status,
                        {},
                        { customHandleErrorCodes: [ECode.EXCEED_FREQUENCY_LIMIT] }
                    )
                        .then((res) => {
                            const { single: isSingleTenant, current_corp_url } = res.data;
                            // 单租户环境下直接跳转至企业自定义登录页
                            if (isSingleTenant) {
                                const targetUrl = (() => {
                                    const singleTenantCustomLoginUrl = current_corp_url;
                                    const redirectUri = getUrlSearchParams(
                                        URL_PARAMETER.REDIRECT_URI
                                    );
                                    if (redirectUri) {
                                        return insertURLQuery(singleTenantCustomLoginUrl, [
                                            {
                                                key: URL_PARAMETER.REDIRECT_URI,
                                                value: encodeURIComponent(redirectUri)
                                            }
                                        ]);
                                    }

                                    return singleTenantCustomLoginUrl;
                                })();

                                return redirect(targetUrl);
                            }

                            return callback?.();
                        })
                        .catch((err) => {
                            const errCode = err.response?.data?.code;
                            if (ECode.EXCEED_FREQUENCY_LIMIT === errCode) {
                                Message.warning(
                                    Intl.get('fxp.base.workspace.login.too_many_submissions.tip')
                                );
                            }
                        });
                }
            };
        default:
            return {};
    }
};

/**
 * 获取当前登录方式可登录的套件列表
 * @param loginType
 */
export const getAllowSelectedSuites = (loginType: string) => {
    return window.fr_integrate_login_options?.urls?.[
        loginType as keyof NonNullable<
            NonNullable<typeof window.fr_integrate_login_options>['urls']
        >
    ];
};

export const getLoginTitle = (loginType: LoginType) => {
    switch (loginType) {
        case LoginType.Wxwork: {
            return Intl.get('account.signin.wework');
        }
        case LoginType.Lark: {
            return Intl.get('custom.login.lark');
        }
        case LoginType.Dingtalk: {
            return Intl.get('account.signin.dingtalk');
        }
        default: {
            return '';
        }
    }
};

export const redirectToLogin = () => {
    const redirect_uri = getUrlParameter(URL_PARAMETER.REDIRECT_URI);
    const suite_id = window.platform_suite_id;
    const suite_metadata = window.platform_suite_metadata;
    const odmId = getOdmId();
    const url = getQueryRouter(
        compact([
            !isEmpty(redirect_uri) && {
                key: URL_PARAMETER.REDIRECT_URI,
                value: encodeURIComponent(redirect_uri)
            },
            isNumber(suite_id) && {
                key: URL_PARAMETER.SUITE_ID,
                value: suite_id
            },
            suite_metadata && {
                key: URL_PARAMETER.SUITE_METADATA,
                value: suite_metadata
            },
            odmId && {
                key: URL_PARAMETER.ODM_ID,
                value: odmId
            }
        ]),
        SITE_ROUTER.AUTHENTICATION.SIGNIN
    );
    redirect(url);
};
